import { Link } from '@tanstack/react-router';
import { CASINO_NAV_ITEMS, casinoNavSlug, type CasinoCategory, type CasinoNavItem } from '@/domain';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/ui/common';

const NAV_ICONS: Record<string, IconName> = {
  lobby: 'home',
  hot: 'populargames',
  slots: 'casino',
  livecasino: 'livecasino',
  gameshows: 'monitor-play',
  tablegames: 'all-games',
  blackjack: 'card',
  roulette: 'casino',
  baccarat: 'card',
  favorites: 'star',
};

function navIcon(id: string): IconName {
  return NAV_ICONS[id] ?? 'casino';
}

export interface CasinoCategoryNavProps {
  activeId: string;
  categories?: CasinoCategory[];
  showFavorites?: boolean;
  className?: string;
}

function isVisible(item: CasinoNavItem, categoryNames: Set<string>, showFavorites: boolean): boolean {
  if (item.favorites) return showFavorites;
  if (!item.requireCategory) return true;
  return categoryNames.has(casinoNavSlug(item.requireCategory));
}

export function CasinoCategoryNav({
  activeId,
  categories = [],
  showFavorites = false,
  className,
}: CasinoCategoryNavProps) {
  const categoryNames = new Set(categories.map((c) => casinoNavSlug(c.CategoryName)));
  // When categories API is empty/unavailable, show all non-favorite items.
  const hasCategoryData = categoryNames.size > 0;
  const items = CASINO_NAV_ITEMS.filter((item) => {
    if (item.favorites) return showFavorites;
    if (!hasCategoryData || !item.requireCategory) return true;
    return isVisible(item, categoryNames, showFavorites);
  });

  return (
    <nav
      className={cn(
        'flex gap-2 overflow-x-auto rounded-md bg-bg-surface px-3 py-2 scrollbar-none',
        className,
      )}
      aria-label="Casino categories"
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <Link
            key={item.id}
            to={item.to}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-body-s tracking-[0.15px]',
              active
                ? 'bg-action-primary font-bold text-text-on-action'
                : 'text-text-secondary hover:bg-bg-elevated',
            )}
          >
            <Icon name={navIcon(item.id)} size={24} className="shrink-0" />
            <span className="whitespace-nowrap">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
